import { useCallback } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { match, P } from "ts-pattern";

import { dayjs } from "@thalamos/common";
import { SubmissionButton } from "@thalamos/frontend";
import type {
  IncidentForm,
  IncidentWorkItemWithContext,
  RecordPolicePresenceMinimumFields,
  RequestPolicePresenceMinimumFields,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentWorkItemIdSchema,
} from "@thalamos/vision-common";

import { apiHooks } from "../../api/index.js";
import { renderUkDateTime } from "../../components/util.js";
import { Error as ErrorComponent } from "../../pages/Error/Error.js";
import { createRoute } from "../../routing/createRoute.js";
import { logActionToDatadog } from "../../tracing.js";

export function getPolicePresenceResponseText(
  hasRemainedAtPlaceOfSafety?: RecordPolicePresenceMinimumFields["hasRemainedAtPlaceOfSafety"]["value"],
): string {
  return match(hasRemainedAtPlaceOfSafety)
    .with(
      P.union(
        "yes",
        "yes-stayed-agree-with-nhs",
        "yes-stayed-disagree-with-nhs",
      ),
      () => "Remained at the place of safety",
    )
    .with(
      P.union("no", "no-escalated-and-left"),
      () => "You have left the place of safety",
    )
    .with(P.nullish, () => "No response recorded")
    .otherwise(() => "No response recorded");
}

type PolicePresenceLogPageProps = Record<string, never>;

export function PolicePresenceLogEntryCard(props: { form: IncidentForm }) {
  const requestSection = props.form.sections.find(
    (section) =>
      section.id === FormSectionSchema.enum.requestPolicePresenceMain,
  );

  const requestData = requestSection?.data as
    RequestPolicePresenceMinimumFields | undefined;

  const responseSection = props.form.sections.find(
    (section) => section.id === FormSectionSchema.enum.recordPolicePresence,
  );

  const responseData = responseSection?.data as
    RecordPolicePresenceMinimumFields | undefined;

  const nhsColor = "rgb(74, 163, 114)";
  const policeColor = "rgb(18, 50, 155)";

  const isNhsRequest = match(requestData?.source?.value)
    .with("mhf", () => false)
    .with("emha", undefined, () => true)
    .exhaustive();

  const healthProfessionalName =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (requestData as any)?.healthProfessionalName?.value;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reasoning = (responseData as any)?.reasoning?.value;

  return (
    requestData && (
      <Stack sx={{ mb: 2, gap: 1 }}>
        {isNhsRequest ? (
          <Box>
            <Typography component="span">
              {requestData.placeOfSafety.name}
            </Typography>
            {requestData.emhaUserName && (
              <Typography component="span">
                {" - "}
                {requestData.emhaUserName.value}
              </Typography>
            )}
          </Box>
        ) : (
          requestData.mhfUserName?.value && (
            <Typography>{requestData.mhfUserName.value}</Typography>
          )
        )}
        <Stack
          sx={{
            gap: 1,
            marginLeft: isNhsRequest ? 4 : 0,
            marginRight: isNhsRequest ? 0 : 4,
          }}
        >
          <Card
            key={`${props.form.id}-request`}
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: 2,
              border: 4,
              borderRadius: 4,
              borderColor: isNhsRequest ? nhsColor : policeColor,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography component="span">Police presence:</Typography>
              <Typography
                component="span"
                sx={{
                  fontWeight: "bold",
                }}
              >
                {match(requestData.hasPolicePresenceRequested.value)
                  .with("yes", () => "Requested to stay")
                  .with("no", () => "Not needed")
                  .with("already-left", () => "Already left")
                  .exhaustive()}
              </Typography>
            </Box>
            {healthProfessionalName && (
              <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
                <Typography component="span">Requested by:</Typography>
                <Typography component="span">
                  {healthProfessionalName}
                </Typography>
              </Box>
            )}
            {requestData.policePresenceRequestedReason?.valueLabel && (
              <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
                <Typography component="span">
                  {requestData.policePresenceRequestedReason.valueLabel}
                </Typography>
              </Box>
            )}
            {requestData.furtherReasoning && (
              <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
                <Typography component="span">
                  {`"${requestData.furtherReasoning.value}"`}
                </Typography>
              </Box>
            )}
          </Card>
          {responseSection && responseData?.hasRemainedAtPlaceOfSafety && (
            <Card
              key={`${props.form.id}-response`}
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: 2,
                border: 4,
                borderRadius: 4,
                borderColor: isNhsRequest ? nhsColor : policeColor,
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography component="span">Police presence:</Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  {isNhsRequest
                    ? match(responseData.hasRemainedAtPlaceOfSafety.value)
                        .with("yes", () => "Police will stay at POS")
                        .with("no", () => "Police will not stay at POS")
                        .otherwise(
                          () =>
                            responseData.hasRemainedAtPlaceOfSafety.valueLabel,
                        )
                    : responseData.hasRemainedAtPlaceOfSafety.valueLabel}
                </Typography>
              </Box>
              {responseData.officerName && (
                <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
                  <Typography component="span">Confirmed by:</Typography>
                  <Typography component="span">
                    {responseData.officerName.value}
                  </Typography>
                </Box>
              )}
              {reasoning && (
                <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
                  <Typography component="span">{`"${String(reasoning)}"`}</Typography>
                </Box>
              )}
            </Card>
          )}
          <Typography
            sx={{
              textAlign: "right",
            }}
          >
            {renderUkDateTime(
              dayjs.utc(requestData.createdAt?.value).toDate(),
              true,
            )}
          </Typography>
        </Stack>
      </Stack>
    )
  );
}
export function PolicePresenceLogPageInner(props: {
  incidentWorkItem: IncidentWorkItemWithContext | undefined;
  onYes: () => Promise<void>;
  onNo: () => Promise<void>;
  onBack: () => Promise<void>;
}) {
  if (!props.incidentWorkItem) {
    return <CircularProgress />; // TODO add a skeleton
  }

  const policePresenceForms = props.incidentWorkItem.forms.filter(
    (form) => form.formType === "requestPolicePresence",
  );

  return (
    <Stack sx={{ width: 500, padding: 2 }} spacing={2}>
      <Paper
        elevation={3}
        sx={{
          padding: "1rem",
          position: "sticky",
          top: ["4rem", "4rem", "1rem"],
        }}
      >
        <Stack
          sx={{
            gap: "1rem",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "grid", width: "100%" }}>
            <Box
              sx={{
                gridColumnStart: 1,
                gridRowStart: 1,
                alignSelf: "center",
                justifySelf: "left",
              }}
            >
              <IconButton
                onClick={() => {
                  void props.onBack();
                }}
              >
                <ArrowBack />
              </IconButton>
            </Box>
            <Typography
              sx={{
                gridColumnStart: 1,
                gridRowStart: 1,
                alignSelf: "center",
                justifySelf: "center",
              }}
            >
              Are you still at the Place of Safety?
            </Typography>
          </Box>

          <Box
            sx={{
              width: 200,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <SubmissionButton variant="contained" onClick={props.onNo}>
              No
            </SubmissionButton>
            <SubmissionButton variant="contained" onClick={props.onYes}>
              Yes
            </SubmissionButton>
          </Box>
        </Stack>
      </Paper>
      <Typography
        variant="h6"
        component="div"
        sx={{
          textAlign: "center",
        }}
      >
        Police Presence Request Log
      </Typography>
      {policePresenceForms.length === 0 ? (
        <Typography>No input received</Typography>
      ) : (
        [...policePresenceForms]
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .map((form) => (
            <PolicePresenceLogEntryCard key={form.id} form={form} />
          ))
      )}
    </Stack>
  );
}

export function PolicePresenceLogPage(_props: PolicePresenceLogPageProps) {
  const navigate = useNavigate();
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [{ data: incidentWorkItem, error: incidentError }] =
    apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  const onYes = useCallback(async () => {
    if (incidentWorkItem?.incidentWorkItem.id) {
      logActionToDatadog("Police confirmed - still at place of safety", {
        incidentId: incidentWorkItem.incidentWorkItem.id,
      });
      navigate(
        createRoute.incidentOfficerRequestPolicePresencePage(
          incidentWorkItem.incidentWorkItem.id,
        ),
      );
    }

    return Promise.resolve();
  }, [incidentWorkItem?.incidentWorkItem.id, navigate]);

  const onNo = useCallback(async () => {
    if (incidentWorkItem?.incidentWorkItem.id) {
      logActionToDatadog("Police confirmed - no longer at place of safety", {
        incidentId: incidentWorkItem.incidentWorkItem.id,
      });
      navigate(
        createRoute.incidentPolicePresenceWhatWillYouDoPage(
          incidentWorkItem.incidentWorkItem.id,
        ),
      );
    }
    return Promise.resolve();
  }, [incidentWorkItem?.incidentWorkItem.id, navigate]);

  const onBack = useCallback(async () => {
    if (incidentWorkItem?.incidentWorkItem.id) {
      navigate(
        createRoute.incidentProgressPage(incidentWorkItem.incidentWorkItem.id),
      );
    }
    return Promise.resolve();
  }, [incidentWorkItem?.incidentWorkItem.id, navigate]);

  if (incidentError) {
    return <ErrorComponent />;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <PolicePresenceLogPageInner
        incidentWorkItem={incidentWorkItem}
        onYes={onYes}
        onNo={onNo}
        onBack={onBack}
      />
    </Box>
  );
}
